// PBR Hero slideshow — self-contained vanilla web component.
// Crossfade slides, edge arrows, dots, optional autoplay, pause on hover,
// touch swipe, keyboard, and prefers-reduced-motion aware.

// How long YouTube must report steady playback before its frame is shown:
// the prev/pause/next cluster it paints after a resume auto-hides in ~3s.
const EMBED_REVEAL_MS = 3500;

class PbrHeroSlideshow extends HTMLElement {
  // YouTube iframes being watched → pending reveal timer id (0 = none)
  #embeds = new Map();

  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('.pbr-hero__slide'));
    this.dots = Array.from(this.querySelectorAll('.pbr-hero__dot'));
    this.count = this.slides.length;
    this.index = Math.max(
      0,
      this.slides.findIndex((s) => s.classList.contains('is-active'))
    );
    if (this.index < 0) this.index = 0;

    this.autoplayMs = (parseInt(this.dataset.autoplay, 10) || 0) * 1000;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.prevBtn = this.querySelector('.pbr-hero__arrow--prev');
    this.nextBtn = this.querySelector('.pbr-hero__arrow--next');
    this.viewport = this.querySelector('.pbr-hero__viewport');

    this.onEmbedMessage = (event) => this.#onEmbedMessage(event);
    window.addEventListener('message', this.onEmbedMessage);
    this.querySelectorAll('.pbr-hero__iframe[src]').forEach((frame) => this.#watchEmbed(frame));

    this.#bind();
    this.#startAutoplay();

    // Videos carry no autoplay attribute, so under reduced motion nothing
    // starts on its own and every slide stays on its poster frame.
    if (!this.reducedMotion) this.#activateMedia(this.slides[this.index]);
  }

  disconnectedCallback() {
    this.#stopAutoplay();
    window.removeEventListener('visibilitychange', this.onVisibility);
    window.removeEventListener('message', this.onEmbedMessage);
    this.#embeds.forEach((timer) => clearTimeout(timer));
    this.#embeds.clear();
  }

  #bind() {
    if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.#step(-1));
    if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.#step(1));

    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const i = parseInt(dot.dataset.index, 10) || 0;
        this.#goTo(i);
        this.#restartAutoplay();
      });
    });

    // Pause on hover / focus
    this.addEventListener('pointerenter', () => this.#stopAutoplay());
    this.addEventListener('pointerleave', () => this.#startAutoplay());
    this.addEventListener('focusin', () => this.#stopAutoplay());
    this.addEventListener('focusout', () => this.#startAutoplay());

    // Keyboard nav on arrows container
    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') {
        this.#step(-1);
        this.#restartAutoplay();
      } else if (e.key === 'ArrowRight') {
        this.#step(1);
        this.#restartAutoplay();
      }
    });

    // Touch swipe
    if (this.viewport) {
      let startX = 0;
      let startY = 0;
      let tracking = false;
      this.viewport.addEventListener(
        'touchstart',
        (e) => {
          tracking = true;
          startX = e.touches[0].clientX;
          startY = e.touches[0].clientY;
        },
        { passive: true }
      );
      this.viewport.addEventListener(
        'touchend',
        (e) => {
          if (!tracking) return;
          tracking = false;
          const dx = e.changedTouches[0].clientX - startX;
          const dy = e.changedTouches[0].clientY - startY;
          if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy)) {
            this.#step(dx < 0 ? 1 : -1);
            this.#restartAutoplay();
          }
        },
        { passive: true }
      );
    }

    // Pause when tab hidden
    this.onVisibility = () => {
      if (document.hidden) this.#stopAutoplay();
      else this.#startAutoplay();
    };
    window.addEventListener('visibilitychange', this.onVisibility);
  }

  #step(dir) {
    this.#goTo((this.index + dir + this.count) % this.count);
  }

  #goTo(next) {
    if (next === this.index || this.count <= 1) return;

    const current = this.slides[this.index];
    const target = this.slides[next];

    if (current) {
      current.classList.remove('is-active');
      current.setAttribute('aria-hidden', 'true');
    }
    if (target) {
      target.classList.add('is-active');
      target.removeAttribute('aria-hidden');
    }

    this.dots.forEach((dot, i) => {
      const active = i === next;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-selected', active ? 'true' : 'false');
    });

    if (!this.reducedMotion) {
      this.#deactivateMedia(current);
      this.#activateMedia(target);
    }

    this.index = next;
  }

  // Background videos load lazily: non-first slides render <source data-src>
  // (and iframes data-src) that only attach the first time the slide shows.
  #activateMedia(slide) {
    if (!slide) return;

    const video = slide.querySelector('.pbr-hero__video');
    if (video) {
      const lazySources = video.querySelectorAll('source[data-src]');
      if (lazySources.length) {
        lazySources.forEach((source) => {
          source.src = source.dataset.src;
          source.removeAttribute('data-src');
        });
        video.load();
      }
      // Playback is started here rather than with an <video autoplay> attribute:
      // WebKit forces native playback controls onto autoplay videos it refuses
      // to start (Low Power Mode, thermal throttling). A refused play() just
      // leaves the poster frame showing.
      video.play().catch(() => {});
    }

    const frame = slide.querySelector('.pbr-hero__iframe[data-src]');
    if (frame) {
      frame.src = frame.dataset.src;
      frame.removeAttribute('data-src');
      this.#watchEmbed(frame);
    }
  }

  // YouTube's embed paints its own UI — a buffering spinner, then a
  // previous / pause / next cluster — every time playback stalls and resumes,
  // and controls=0 does not suppress it. So a YouTube frame starts transparent
  // (the slide's poster shows through) and is only faded in once YouTube has
  // reported steady playback for EMBED_REVEAL_MS; any other state hides it
  // again. State arrives over the IFrame API's postMessage channel, which
  // needs no API script: post "listening" to the frame and YouTube replies
  // with onStateChange / infoDelivery messages. Vimeo's background mode draws
  // no UI, so Vimeo frames are left alone.
  #watchEmbed(frame) {
    if (!frame || frame.dataset.provider !== 'youtube' || this.#embeds.has(frame)) return;
    this.#embeds.set(frame, 0);

    const listen = () => {
      frame.contentWindow?.postMessage(
        JSON.stringify({ event: 'listening', id: 'pbr-hero', channel: 'widget' }),
        'https://www.youtube.com'
      );
    };
    frame.addEventListener('load', listen);
    listen(); // the eager first-slide frame may have loaded before this script ran
  }

  #onEmbedMessage(event) {
    if (event.origin !== 'https://www.youtube.com') return;
    let frame = null;
    for (const candidate of this.#embeds.keys()) {
      if (candidate.contentWindow === event.source) frame = candidate;
    }
    if (!frame) return;

    let data;
    try {
      data = JSON.parse(event.data);
    } catch {
      return;
    }

    // 1 = playing; -1 unstarted, 0 ended, 2 paused, 3 buffering, 5 cued
    let state = null;
    if (data.event === 'onStateChange') state = data.info;
    else if (data.event === 'infoDelivery' && typeof data.info?.playerState === 'number') {
      state = data.info.playerState;
    }
    if (typeof state !== 'number') return;

    const timer = this.#embeds.get(frame);
    if (state === 1) {
      if (!timer && !frame.classList.contains('is-playing')) {
        this.#embeds.set(
          frame,
          setTimeout(() => {
            this.#embeds.set(frame, 0);
            frame.classList.add('is-playing');
          }, EMBED_REVEAL_MS)
        );
      }
    } else {
      clearTimeout(timer);
      this.#embeds.set(frame, 0);
      frame.classList.remove('is-playing');
    }
  }

  #deactivateMedia(slide) {
    slide?.querySelector('.pbr-hero__video')?.pause();
  }

  #startAutoplay() {
    this.#stopAutoplay();
    if (this.autoplayMs <= 0 || this.count <= 1 || this.reducedMotion) return;
    this.timer = window.setInterval(() => this.#step(1), this.autoplayMs);
  }

  #stopAutoplay() {
    if (this.timer) {
      window.clearInterval(this.timer);
      this.timer = null;
    }
  }

  #restartAutoplay() {
    this.#startAutoplay();
  }
}

if (!customElements.get('pbr-hero-slideshow')) {
  customElements.define('pbr-hero-slideshow', PbrHeroSlideshow);
}
